import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import {
  PURCHASE_NUMBER_ERROR_CREDENTIALS,
  PURCHASE_NUMBER_ERROR_INSUFFICIENT_FUNDS,
  PURCHASE_NUMBER_ERROR_FORBIDDEN,
  PURCHASE_NUMBER_ERROR_DUPLICATE,
  PURCHASE_NUMBER_ERROR_NUMBER_TAKEN,
  PURCHASE_NUMBER_ERROR_UNREACHABLE,
  PURCHASE_NUMBER_ERROR_INVALID_SID,
  PURCHASE_NUMBER_ERROR_INVALID_COUNTRY,
  PURCHASE_NUMBER_ERROR_INVALID_PHONE,
  PURCHASE_NUMBER_ERROR_INVALID_NUMBER_TYPE,
  PURCHASE_NUMBER_ERROR_INVALID_REQUEST,
  PURCHASE_NUMBER_ERROR_ACCOUNT_NOT_FOUND,
  PURCHASE_NUMBER_ERROR_MISSING_FIELDS,
  PURCHASE_NUMBER_ERROR_CONTAINS_LENGTH,
  PURCHASE_NUMBER_ERROR_ROUTE_FAILED,
  PURCHASE_NUMBER_ERROR_BUSINESS_NAME_REQUIRED,
  PURCHASE_NUMBER_ERROR_ACCOUNT_REQUIRED,
  PURCHASE_NUMBER_ERROR_COUNTRY_REQUIRED,
  PURCHASE_NUMBER_ERROR_UNEXPECTED,
  PURCHASE_NUMBER_ERROR_BUNDLE_REQUIRED,
  PURCHASE_NUMBER_NO_RESULTS,
  PURCHASE_NUMBER_PHONE_NUMBER,
  PURCHASE_NUMBER_LOCALITY,
  PURCHASE_NUMBER_CAPABILITIES,
  PURCHASE_NUMBER_SELECT,
  PURCHASE_NUMBER_ACCOUNT,
  PURCHASE_NUMBER_BUSINESS_NAME,
  PURCHASE_NUMBER_ACCOUNT_SID,
  PURCHASE_NUMBER_ACCOUNT_SID_DEFAULT,
  PURCHASE_NUMBER_SUCCESS,
  PURCHASE_NUMBER_CONTINUE,
  trans
} from '../../translator'

const SID_PATTERN = /^AC[a-f0-9]{32}$/
const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/
const MAX_CONTAINS_LENGTH = 10

// Maps the API's stable, language-agnostic error codes to translation keys.
// The server never sends a human-readable message, so all copy is translated
// client-side from the code — keeping non-English admins localised.
const ERROR_CODE_MESSAGES = {
  forbidden: PURCHASE_NUMBER_ERROR_FORBIDDEN,
  invalid_request_body: PURCHASE_NUMBER_ERROR_INVALID_REQUEST,
  missing_fields: PURCHASE_NUMBER_ERROR_MISSING_FIELDS,
  invalid_phone_number: PURCHASE_NUMBER_ERROR_INVALID_PHONE,
  invalid_country_code: PURCHASE_NUMBER_ERROR_INVALID_COUNTRY,
  invalid_sid: PURCHASE_NUMBER_ERROR_INVALID_SID,
  invalid_number_type: PURCHASE_NUMBER_ERROR_INVALID_NUMBER_TYPE,
  contains_too_long: PURCHASE_NUMBER_ERROR_CONTAINS_LENGTH,
  account_not_found: PURCHASE_NUMBER_ERROR_ACCOUNT_NOT_FOUND,
  invalid_credentials: PURCHASE_NUMBER_ERROR_CREDENTIALS,
  duplicate: PURCHASE_NUMBER_ERROR_DUPLICATE,
  insufficient_funds: PURCHASE_NUMBER_ERROR_INSUFFICIENT_FUNDS,
  number_unavailable: PURCHASE_NUMBER_ERROR_NUMBER_TAKEN,
  bundle_required: PURCHASE_NUMBER_ERROR_BUNDLE_REQUIRED,
  route_creation_failed: PURCHASE_NUMBER_ERROR_ROUTE_FAILED,
  provider_unreachable: PURCHASE_NUMBER_ERROR_UNREACHABLE,
  unexpected: PURCHASE_NUMBER_ERROR_UNEXPECTED
}

function messageForErrorCode (code) {
  const key = ERROR_CODE_MESSAGES[code] || PURCHASE_NUMBER_ERROR_UNEXPECTED
  return trans(key, {}, 'sms')
}

export default class extends Controller {
  static values = {
    accounts: Array
  }

  static targets = [
    'credentialsSection',
    'resultsSection',
    'confirmSection',
    'accountSid',
    'authToken',
    'businessName',
    'accountSelect',
    'countryCodeSelect',
    'countryCodeInput',
    'numberType',
    'contains',
    'searchButton',
    'confirmButton',
    'resultsList',
    'confirmDetails',
    'errorMessage',
    'warningMessage',
    'loadingIndicator'
  ]

  connect () {
    this.selectedNumber = null
    this.selectedLocality = null
  }

  selectAccount (event) {
    const accountId = event.target.value
    const account = this.accountsValue.find(a => a.id === accountId)
    if (account) {
      this.businessNameTarget.value = account.name
    }
  }

  selectCountryCode (event) {
    const value = event.target.value
    if (value === 'other') {
      this.countryCodeInputTarget.classList.remove('d-none')
      this.countryCodeInputTarget.value = ''
      this.countryCodeInputTarget.focus()
    } else {
      this.countryCodeInputTarget.classList.add('d-none')
      this.countryCodeInputTarget.value = ''
    }
  }

  getCountryCode () {
    const selectValue = this.countryCodeSelectTarget.value
    if (selectValue === 'other') {
      return this.countryCodeInputTarget.value.trim().toUpperCase()
    }
    return selectValue
  }

  async search () {
    this.clearErrors()

    if (!this.validateSearchForm()) {
      return
    }

    this.searchButtonTarget.disabled = true
    this.showLoadingIndicator(true)
    this.resultsSectionTarget.classList.remove('d-none')
    this.resultsListTarget.innerHTML = ''
    this.confirmSectionTarget.classList.add('d-none')

    try {
      const response = await fetchWithHandlingLoginRedirect('/internal/api/sms-routes/search-available', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          countryCode: this.getCountryCode(),
          numberType: this.numberTypeTarget.value,
          contains: this.containsTarget.value.trim() || null,
          accountSid: this.accountSidTarget.value.trim() || null,
          authToken: this.authTokenTarget.value.trim() || null
        })
      })

      const data = await response.json()
      this.renderResults(data.results || [])
    } catch (error) {
      await this.handleError(error)
    } finally {
      this.searchButtonTarget.disabled = false
      this.showLoadingIndicator(false)
    }
  }

  selectNumber (event) {
    const button = event.currentTarget
    this.selectedNumber = button.dataset.phoneNumber
    this.selectedLocality = button.dataset.locality

    // Clear any previous selection highlight
    this.resultsListTarget.querySelectorAll('tr.table-active').forEach(el => el.classList.remove('table-active'))

    // Highlight the selected row
    const row = button.closest('tr')
    if (row) {
      row.classList.add('table-active')
    }

    this.renderConfirmation()
    this.confirmSectionTarget.classList.remove('d-none')
    this.scrollToElement(this.confirmSectionTarget)
  }

  cancelSelection () {
    this.clearErrors()
    this.selectedNumber = null
    this.selectedLocality = null
    this.confirmSectionTarget.classList.add('d-none')

    // Clear selection highlight
    this.resultsListTarget.querySelectorAll('tr.table-active').forEach(el => el.classList.remove('table-active'))
  }

  async confirmPurchase () {
    this.clearErrors()
    this.confirmButtonTarget.disabled = true

    try {
      const response = await fetchWithHandlingLoginRedirect('/internal/api/sms-routes/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: this.selectedNumber,
          countryCode: this.getCountryCode(),
          accountId: this.accountSelectTarget.value,
          accountSid: this.accountSidTarget.value.trim() || null,
          authToken: this.authTokenTarget.value.trim() || null,
          businessDisplayName: this.businessNameTarget.value.trim()
        })
      })

      const data = await response.json()

      if (data.warnings && data.warnings.length > 0) {
        this.showWarnings(data.warnings)
        this.showSuccessWithWarnings()
      } else {
        window.location.href = '/sms-routes'
      }
    } catch (error) {
      this.confirmButtonTarget.disabled = false

      if (error.status === 410 || error.status === 409) {
        this.confirmSectionTarget.classList.add('d-none')
      } else if (error.status === 401) {
        this.confirmSectionTarget.classList.add('d-none')
      }

      await this.handleError(error)
    }
  }

  cancel () {
    window.location.href = '/sms-routes'
  }

  // --- Validation ---

  validateSearchForm () {
    let isValid = true

    // Credentials are optional: blank means "use the Raven default" (resolved
    // server-side). A typed SID must still be well-formed.
    if (this.accountSidTarget.value.trim() && !SID_PATTERN.test(this.accountSidTarget.value.trim())) {
      this.showFieldError(this.accountSidTarget, trans(PURCHASE_NUMBER_ERROR_INVALID_SID, {}, 'sms'))
      isValid = false
    }

    if (!this.businessNameTarget.value.trim()) {
      this.showFieldError(this.businessNameTarget, trans(PURCHASE_NUMBER_ERROR_BUSINESS_NAME_REQUIRED, {}, 'sms'))
      isValid = false
    }

    if (!this.accountSelectTarget.value) {
      this.showFieldError(this.accountSelectTarget, trans(PURCHASE_NUMBER_ERROR_ACCOUNT_REQUIRED, {}, 'sms'))
      isValid = false
    }

    const countryCode = this.getCountryCode()
    if (!countryCode) {
      this.showFieldError(
        this.countryCodeSelectTarget.value === 'other' ? this.countryCodeInputTarget : this.countryCodeSelectTarget,
        trans(PURCHASE_NUMBER_ERROR_COUNTRY_REQUIRED, {}, 'sms')
      )
      isValid = false
    } else if (!COUNTRY_CODE_PATTERN.test(countryCode)) {
      this.showFieldError(
        this.countryCodeInputTarget,
        trans(PURCHASE_NUMBER_ERROR_INVALID_COUNTRY, {}, 'sms')
      )
      isValid = false
    }

    const containsValue = this.containsTarget.value.trim()
    if (containsValue && containsValue.length > MAX_CONTAINS_LENGTH) {
      this.showFieldError(this.containsTarget, trans(PURCHASE_NUMBER_ERROR_CONTAINS_LENGTH, {}, 'sms'))
      isValid = false
    }

    return isValid
  }

  showFieldError (field, message) {
    const existingError = field.parentElement.querySelector('.field-error')
    if (existingError) {
      existingError.remove()
    }

    const errorEl = document.createElement('div')
    errorEl.className = 'field-error text-danger small mt-1'
    errorEl.textContent = message
    field.parentElement.appendChild(errorEl)
  }

  clearFieldErrors () {
    this.element.querySelectorAll('.field-error').forEach(el => el.remove())
  }

  // --- Rendering ---

  renderResults (results) {
    if (results.length === 0) {
      this.resultsListTarget.innerHTML = `<div class="text-center text-muted py-4">${escapeHtml(trans(PURCHASE_NUMBER_NO_RESULTS, {}, 'sms'))}</div>`
      return
    }

    const phoneNumberHeader = escapeHtml(trans(PURCHASE_NUMBER_PHONE_NUMBER, {}, 'sms'))
    const localityHeader = escapeHtml(trans(PURCHASE_NUMBER_LOCALITY, {}, 'sms'))
    const capabilitiesHeader = escapeHtml(trans(PURCHASE_NUMBER_CAPABILITIES, {}, 'sms'))
    const selectLabel = escapeHtml(trans(PURCHASE_NUMBER_SELECT, {}, 'sms'))

    const rows = results.map(result => {
      const capabilities = []
      if (result.smsCapable) capabilities.push('<span class="badge badge-success mr-1">SMS</span>')
      if (result.voiceCapable) capabilities.push('<span class="badge badge-info mr-1">Voice</span>')
      if (result.mmsCapable) capabilities.push('<span class="badge badge-secondary mr-1">MMS</span>')

      return `
        <tr>
          <td>${escapeHtml(result.phoneNumber)}</td>
          <td>${escapeHtml(result.locality || '')}</td>
          <td>${capabilities.join('')}</td>
          <td class="text-right">
            <button type="button"
              class="btn btn-sm btn-raven-light"
              data-action="purchase-number#selectNumber"
              data-phone-number="${escapeHtml(result.phoneNumber)}"
              data-locality="${escapeHtml(result.locality || '')}">
              ${selectLabel}
            </button>
          </td>
        </tr>`
    }).join('')

    this.resultsListTarget.innerHTML = `
      <table class="table table-hover">
        <thead>
          <tr>
            <th>${phoneNumberHeader}</th>
            <th>${localityHeader}</th>
            <th>${capabilitiesHeader}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`
  }

  renderConfirmation () {
    const account = this.accountsValue.find(a => a.id === this.accountSelectTarget.value)
    const accountName = account ? escapeHtml(account.name) : ''
    const sidValue = this.accountSidTarget.value.trim()
    const maskedSid = sidValue
      ? escapeHtml(sidValue.substring(0, 6) + '...')
      : escapeHtml(trans(PURCHASE_NUMBER_ACCOUNT_SID_DEFAULT, {}, 'sms'))

    const phoneNumberLabel = escapeHtml(trans(PURCHASE_NUMBER_PHONE_NUMBER, {}, 'sms'))
    const localityLabel = escapeHtml(trans(PURCHASE_NUMBER_LOCALITY, {}, 'sms'))
    const accountLabel = escapeHtml(trans(PURCHASE_NUMBER_ACCOUNT, {}, 'sms'))
    const businessNameLabel = escapeHtml(trans(PURCHASE_NUMBER_BUSINESS_NAME, {}, 'sms'))
    const accountSidLabel = escapeHtml(trans(PURCHASE_NUMBER_ACCOUNT_SID, {}, 'sms'))

    this.confirmDetailsTarget.innerHTML = `
      <table class="table align-items-center table-flush mb-0">
        <tbody>
          <tr>
            <td class="text-muted rtext-sm" style="width: 200px">${phoneNumberLabel}</td>
            <td><span class="name mb-0 item-info rtext-sm">${escapeHtml(this.selectedNumber)}</span></td>
          </tr>
          <tr>
            <td class="text-muted rtext-sm">${localityLabel}</td>
            <td><span class="name mb-0 item-info rtext-sm">${escapeHtml(this.selectedLocality || 'N/A')}</span></td>
          </tr>
          <tr>
            <td class="text-muted rtext-sm">${accountLabel}</td>
            <td><span class="name mb-0 item-info rtext-sm">${accountName}</span></td>
          </tr>
          <tr>
            <td class="text-muted rtext-sm">${businessNameLabel}</td>
            <td><span class="name mb-0 item-info rtext-sm">${escapeHtml(this.businessNameTarget.value.trim())}</span></td>
          </tr>
          <tr>
            <td class="text-muted rtext-sm">${accountSidLabel}</td>
            <td><span class="name mb-0 item-info rtext-sm">${maskedSid}</span></td>
          </tr>
        </tbody>
      </table>`
  }

  // --- Error / Warning Handling ---

  async handleError (error) {
    let message = trans(PURCHASE_NUMBER_ERROR_UNEXPECTED, {}, 'sms')

    if (error && error.status) {
      try {
        const data = await error.json()
        // The API returns a stable, language-agnostic error code; translate it
        // client-side rather than displaying any server-provided text.
        if (data && data.errorCode) {
          message = messageForErrorCode(data.errorCode)
        }
      } catch {
        // Response body not readable — fall back to the generic message.
      }
    }

    this.showError(message)
  }

  showError (message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove('d-none')
    this.scrollToElement(this.errorMessageTarget)
  }

  showWarnings (warnings) {
    this.warningMessageTarget.innerHTML = warnings.map(w => `<div>${escapeHtml(w)}</div>`).join('')
    this.warningMessageTarget.classList.remove('d-none')
    this.scrollToElement(this.warningMessageTarget)
  }

  showSuccessWithWarnings () {
    this.confirmSectionTarget.innerHTML = `
        <hr>
        <div class="alert alert-success" role="alert">
            ${escapeHtml(trans(PURCHASE_NUMBER_SUCCESS, { '%number%': this.selectedNumber }, 'sms'))}
        </div>
        <div class="mt-3">
            <a href="/sms-routes" class="btn btn-raven-light btn-md">
                ${escapeHtml(trans(PURCHASE_NUMBER_CONTINUE, {}, 'sms'))}
            </a>
        </div>
    `
    this.confirmSectionTarget.classList.remove('d-none')
    this.scrollToElement(this.warningMessageTarget)
  }

  clearErrors () {
    this.errorMessageTarget.textContent = ''
    this.errorMessageTarget.classList.add('d-none')
    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.innerHTML = ''
      this.warningMessageTarget.classList.add('d-none')
    }
    this.clearFieldErrors()
  }

  // --- Scrolling ---

  scrollToElement (element, offset = 120) {
    const rect = element.getBoundingClientRect()
    const absoluteTop = rect.top + window.pageYOffset
    window.scrollTo({ top: absoluteTop - offset, behavior: 'smooth' })
  }

  // --- Loading ---

  showLoadingIndicator (show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('d-none', !show)
    }
  }
}
